import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  IconButton,
  Divider,
  Card,
  CardContent,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { FUEL_GRADES, ITEM_CATEGORIES } from '../constants';
import ReceiptTypeSwitch from './ReceiptTypeSwitch';


const normalizeItems = (items, receiptType) => (items || []).map((item) => {
  if (receiptType === 'fuel') {
    return {
      ...item,
      category: 'fuel',
      unit: 'L',
      fuel_grade: FUEL_GRADES.some((grade) => grade.id === item.fuel_grade)
        ? item.fuel_grade
        : 'other',
    };
  }

  const category = ITEM_CATEGORIES.find(
    (candidate) => candidate.name === item.category || candidate.id === item.category
  );
  return {
    ...item,
    category: category?.id && category.id !== 'fuel' ? category.id : 'other',
    fuel_grade: null,
  };
});


function ReceiptReview({ receiptData, onApprove, onCancel }) {
  const [editableData, setEditableData] = useState(null);
  const [isSaving, setIsSaving] = useState(false); 
  const [isReprocessing, setIsReprocessing] = useState(false);

  useEffect(() => {
    if (receiptData) {
      // Handle the new nested structure from /api/upload-receipt
      if (receiptData.extracted_data && receiptData.extracted_data.parsed_data && receiptData.image_filename) {
        const { parsed_data, token_usage } = receiptData.extracted_data;
        const receiptType = parsed_data.receiptType || receiptData.receipt_type || 'grocery';
        setEditableData({
          ...JSON.parse(JSON.stringify(parsed_data)), // The editable receipt fields
          receiptType,
          image_filename: receiptData.image_filename,
          items: normalizeItems(parsed_data.items, receiptType),
          // Store token usage to be sent back on save
          total_tokens: token_usage ? token_usage.total_tokens : null,
          prompt_tokens: token_usage ? token_usage.prompt_tokens : null,
          thoughts_tokens: token_usage ? token_usage.thoughts_tokens : null,
        });
      } else if (receiptData.image_filename) { // Fallback for older structure
        const receiptType = receiptData.receiptType || receiptData.receipt_type || 'grocery';
        setEditableData({
            ...JSON.parse(JSON.stringify(receiptData)),
            receiptType,
            items: normalizeItems(receiptData.items, receiptType),
        });
      }
    }
  }, [receiptData]);

  if (!editableData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>
          { isReprocessing ? 'Reprocessing...' : 'Loading review data...' }
        </Typography>
      </Box>
    );
  }

  const handleOverallFieldChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    if (name === "totalAmount") {
        processedValue = parseFloat(value) || 0;
    }
    setEditableData(prevData => ({
      ...prevData,
      [name]: processedValue,
      ...(name === 'receiptType' ? {
        items: normalizeItems(prevData.items, processedValue),
      } : {}),
    }));
  };

  const handleReceiptTypeChange = (receiptType) => {
    setEditableData(prevData => ({
      ...prevData,
      receiptType,
      items: normalizeItems(prevData.items, receiptType),
    }));
  };

  const handleItemChange = (index, field, value) => {
    setEditableData(prevData => {
      const newItems = [...(prevData.items || [])];
      const currentItem = { ...newItems[index] };

      const numericFields = ['quantity', 'price_per_unit', 'total_price', 'discount_amount'];
      if (field === 'is_discounted') {
        currentItem[field] = value; 
      } else if (numericFields.includes(field)) {
        currentItem[field] = parseFloat(value) || 0;
      } else {
        currentItem[field] = value;
      }
      
      newItems[index] = currentItem;
      return { ...prevData, items: newItems };
    });
  };

  const handleAddItem = () => {
    setEditableData(prevData => ({
      ...prevData,
      items: [
        ...(prevData.items || []),
        {
          short_name: '',
          proper_name: '',
          quantity: 1,
          price_per_unit: 0,
          total_price: 0,
          category: prevData.receiptType === 'fuel' ? 'fuel' : 'other',
          unit: prevData.receiptType === 'fuel' ? 'L' : null,
          fuel_grade: prevData.receiptType === 'fuel' ? 'other' : null,
          is_discounted: false,
          discount_amount: 0,
          discount_type: '',
        }
      ]
    }));
  };

  const handleRemoveItem = (index) => {
    setEditableData(prevData => ({
      ...prevData,
      items: (prevData.items || []).filter((_, i) => i !== index)
    }));
  };

  const handleReProcessClick = async () => {
    if (!editableData.image_filename) {
      alert("No image filename available for re-processing.");
      return;
    }

    try {
      setIsReprocessing(true);
      setEditableData(null);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000/api';

      const formData = new FormData();
      formData.append('image_filename', editableData.image_filename);
      formData.append('receipt_type', editableData.receiptType || 'grocery');

      const response = await fetch(`${backendUrl}/upload-receipt`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'An unknown error occurred during re-processing.' }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // Handle the new nested structure from the backend
      const { parsed_data, token_usage } = data.extracted_data;
      const receiptType = parsed_data.receiptType || data.receipt_type || editableData.receiptType || 'grocery';
      setEditableData({
        ...parsed_data,
        receiptType,
        image_filename: data.image_filename,
        items: normalizeItems(parsed_data.items, receiptType),
        total_tokens: token_usage ? token_usage.total_tokens : null,
        prompt_tokens: token_usage ? token_usage.prompt_tokens : null,
        thoughts_tokens: token_usage ? token_usage.thoughts_tokens : null,
      });
    } catch (error) {
      console.error("Error re-processing receipt:", error);
      alert("An error occurred while re-processing the receipt.");
    } finally {
      setIsReprocessing(false);
    }
  };

  const handleApproveClick = async () => {
    setIsSaving(true);
    await onApprove(editableData); // onApprove should handle global processing state
    setIsSaving(false);
  };

  const imageBaseUrl = process.env.REACT_APP_TEMP_URL || '';
  const imageUrl = editableData.image_filename 
    ? `${imageBaseUrl}/${editableData.image_filename}`
    : null;
    

  return (
    <Grid container spacing={3} sx={{ mt: 1 }}>
      <Grid 
        item 
        size={{ xs:12, md:6 }}
        sx={{ 
          position: { md: 'sticky' }, 
          top: theme => theme.spacing(2),
          alignSelf: { md: 'flex-start' }, 
          height: { md: 'calc(100vh - 16px - 16px)' },
          overflowY: { md: 'auto' } 
        }}
      > 
        <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
          {imageUrl ? (
            <Box
              component="img"
              sx={{
                width: '100%',
                maxHeight: 'calc(100% - 48px)', 
                objectFit: 'contain',
                border: '1px solid #ddd',
                borderRadius: 1
              }}
              alt="Receipt"
              src={imageUrl}
              onError={(e) => {
                e.target.onerror = null; // prevent looping
                e.target.alt = "Image not found or failed to load";
              }}
            />
          ) : (
            <Typography color="text.secondary">
              No image filename available for this receipt.
            </Typography>
          )}
        </Paper>
      </Grid>

      <Grid item size={{ xs:12, md:6 }}> 
        <Paper elevation={2} sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item size={{ xs:12 }}>
              <ReceiptTypeSwitch
                value={editableData.receiptType || 'grocery'}
                onChange={handleReceiptTypeChange}
              />
            </Grid>
            <Grid item size={{ xs:12, md:6 }}> 
              <TextField
                label="Store Name"
                name="storeName"
                value={editableData.storeName || ''}
                onChange={handleOverallFieldChange}
                fullWidth
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item size={{ xs:12, md:6 }}> 
              <TextField
                label="Transaction Date"
                name="transactionDate"
                value={editableData.transactionDate || ''}
                onChange={handleOverallFieldChange}
                fullWidth
                variant="outlined"
                size="small"
                placeholder="YYYY-MM-DD"
              />
            </Grid>
            <Grid item size={{ xs:12, md:6 }}> 
              <TextField
                label="Transaction Time"
                name="transactionTime"
                value={editableData.transactionTime || ''}
                onChange={handleOverallFieldChange}
                fullWidth
                variant="outlined"
                size="small"
                placeholder="HH:MM:SS"
              />
            </Grid>
            <Grid item size={{ xs:12, md:6 }}> 
              <TextField
                label="Total Amount"
                name="totalAmount"
                type="number"
                slotProps={{
                  htmlInput: {
                    step: "0.01"
                  },
                  input: { 
                    startAdornment: <InputAdornment position="start">£</InputAdornment>
                  }
                }}
                value={editableData.totalAmount || 0}
                onChange={handleOverallFieldChange}
                fullWidth
                variant="outlined"
                size="small"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }}>
            <Typography variant="subtitle1">Items</Typography>
          </Divider>

          {(editableData.items || []).map((item, index) => (
            <Card variant="outlined" key={index} sx={{ mb: 2 }}>
              <CardContent sx={{pt: 1}}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body1" component="div">
                    <strong>Item {index + 1}</strong>
                  </Typography>
                  <IconButton onClick={() => handleRemoveItem(index)} size="small" color="error">
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <Grid container spacing={2}>
                  <Grid item size={{ xs:12 }}>
                    <TextField label="Name (on receipt)" value={item.short_name || ''} onChange={(e) => handleItemChange(index, 'short_name', e.target.value)} fullWidth size="small" variant="outlined" />
                  </Grid>
                  <Grid item size={{ xs:12 }}>
                    <TextField label="Proper Name" value={item.proper_name || ''} onChange={(e) => handleItemChange(index, 'proper_name', e.target.value)} fullWidth size="small" variant="outlined" />
                  </Grid>
                  <Grid item size={{ xs:12, sm:6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={item.category}
                        label="Category"
                        onChange={(e) => handleItemChange(index, 'category', e.target.value)}
                        disabled={editableData.receiptType === 'fuel'}
                      >
                        {ITEM_CATEGORIES.map((cat) => (
                          <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item size={{ xs:12, sm:6 }}>
                    <TextField
                      label={editableData.receiptType === 'fuel' ? 'Litres' : 'Quantity'}
                      type="number"
                      value={item.quantity ?? 1}
                      onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      slotProps={{ htmlInput: { step: '0.001', min: '0' } }}
                      fullWidth
                      size="small"
                      variant="outlined"
                    />
                  </Grid>
                  {editableData.receiptType === 'fuel' ? (
                    <Grid item size={{ xs:12, sm:6 }}>
                      <FormControl fullWidth size="small" required>
                        <InputLabel>Fuel Grade</InputLabel>
                        <Select
                          value={item.fuel_grade || 'other'}
                          label="Fuel Grade"
                          onChange={(e) => handleItemChange(index, 'fuel_grade', e.target.value)}
                        >
                          {FUEL_GRADES.map((grade) => (
                            <MenuItem key={grade.id} value={grade.id}>{grade.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  ) : (
                    <Grid item size={{ xs:12, sm:6 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Unit</InputLabel>
                        <Select
                          value={item.unit || ''}
                          label="Unit"
                          onChange={(e) => handleItemChange(index, 'unit', e.target.value || null)}
                        >
                          <MenuItem value=""><em>Unspecified</em></MenuItem>
                          <MenuItem value="ea">Each</MenuItem>
                          <MenuItem value="kg">Kilograms</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  <Grid item size={{ xs:12, sm:6 }}>
                    <TextField 
                      label="Price/Unit" 
                      type="number" 
                      slotProps={{
                        htmlInput: {
                          step: editableData.receiptType === 'fuel' ? "0.001" : "0.01"
                        },
                        input: { 
                          startAdornment: <InputAdornment position="start">£</InputAdornment>
                        }
                      }}
                      value={item.price_per_unit || 0} 
                      onChange={(e) => handleItemChange(index, 'price_per_unit', e.target.value)} 
                      fullWidth 
                      size="small" 
                      variant="outlined" 
                    />
                  </Grid>
                  <Grid item size={{ xs:12, sm:6 }}>
                    <TextField 
                      label="Total Price" 
                      type="number" 
                      slotProps={{
                        htmlInput: {
                          step: "0.01"
                        },
                        input: { 
                          startAdornment: <InputAdornment position="start">£</InputAdornment>
                        }
                      }}
                      value={item.total_price || 0} 
                      onChange={(e) => handleItemChange(index, 'total_price', e.target.value)} 
                      fullWidth 
                      size="small" 
                      variant="outlined" 
                    />
                  </Grid>
                  <Grid item size={{ xs:12 }} sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                      control={<Checkbox checked={!!item.is_discounted} onChange={(e) => handleItemChange(index, 'is_discounted', e.target.checked)} />}
                      label="Discounted"
                    />
                  </Grid>
                  <Grid item size={{ xs:12, sm:6 }}>
                    <TextField 
                      label="Discount Amt" 
                      type="number" 
                      slotProps={{
                        htmlInput: {
                          step: "0.01"
                        },
                        input: { 
                          startAdornment: <InputAdornment position="start">£</InputAdornment>
                        }
                      }}
                      value={item.discount_amount || 0} 
                      onChange={(e) => handleItemChange(index, 'discount_amount', e.target.value)} 
                      fullWidth 
                      size="small" 
                      variant="outlined" 
                    />
                  </Grid>
                  <Grid item size={{ xs:12, sm:6 }}>
                     <TextField label="Discount Type" value={item.discount_type || ''} onChange={(e) => handleItemChange(index, 'discount_type', e.target.value)} fullWidth size="small" variant="outlined" />
                  </Grid>
                  
                </Grid>
              </CardContent>
            </Card>
          ))}
          
          <Button
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleAddItem}
            variant="outlined"
            size="small"
            sx={{ mt: 1, mb: 3 }}
          >
            Add Item
          </Button>

          {(!editableData.items || editableData.items.length === 0) && (
            <Typography color="text.secondary" sx={{ my: 2, textAlign: 'center' }}>
              No items listed. You can add items manually.
            </Typography>
          )}
          
          <Grid container spacing={2} justifyContent="flex-end" sx={{ mt: 3, pt: 2, borderTop: '1px solid #eee' }}>
            <Grid item size={{ xs:12, sm:4 }}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={onCancel}
                startIcon={<CancelIcon />}
                disabled={isSaving || isReprocessing}
                fullWidth
              >
                Cancel / Re-upload
              </Button>
            </Grid>
            <Grid item size={{ xs: 12, sm: 4 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleReProcessClick}
                startIcon={isReprocessing ? <CircularProgress size={20} color="inherit" /> : <AutorenewIcon />}
                disabled={isSaving || isReprocessing}
                fullWidth
              >
                {isReprocessing ? 'Re-Processing...' : 'Re-Process Receipt' }
              </Button>
            </Grid>
            <Grid item size={{ xs:12, sm:4 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleApproveClick}
                startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                disabled={isSaving || isReprocessing}
                fullWidth
              >
                {isSaving ? 'Saving...' : 'Approve and Save'}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
}

export default ReceiptReview;
